import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Header, Footer, GigDetails as GD } from '../components/components';
import { getGigBySlug, getSimilarGigs } from '../services/gig.service';
import { getGigReviews } from '../services/review.service';

const GigDetails = () => {
    const { gigSlug } = useParams();

    // Gig data
    const [gig, setGig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Main image carousel
    const [currentImage, setCurrentImage] = useState(0);
    const [autoplay, setAutoplay] = useState(true);
    const autoplayRef = useRef();

    // Similar gigs
    const [similarGigs, setSimilarGigs] = useState([]);

    // Reviews data
    const [reviews, setReviews] = useState([]);
    const [reviewStats, setReviewStats] = useState({
        total: 0,
        average: 0,
        stars: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
    });

    // Fetch gig by slug
    useEffect(() => {
        const fetchGig = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getGigBySlug(gigSlug);
                setGig(data.gig);
                
                // Fetch similar gigs using gig ID
                if (data.gig?._id) {
                    const [similarData, reviewsData] = await Promise.all([
                        getSimilarGigs(data.gig._id),
                        getGigReviews(data.gig._id, { limit: 100 })
                    ]);
                    
                    setSimilarGigs(similarData.gigs || []);
                    setReviews(reviewsData.reviews || []);
                    
                    // Calculate stats
                    const stats = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
                    reviewsData.reviews?.forEach(r => {
                        stats[r.rating] = (stats[r.rating] || 0) + 1;
                    });
                    setReviewStats({
                        total: reviewsData.pagination?.total || reviewsData.reviews?.length || 0,
                        average: data.gig.avgRating || 0,
                        stars: stats
                    });
                }
            } catch (err) {
                console.error("Fetch error:", err);
                setError(err.response?.data?.error || err.message || 'Failed to load gig');
            } finally {
                setLoading(false);
            }
        };

        if (gigSlug) fetchGig();
    }, [gigSlug]);

    // Build gallery images from gig data
    const galleryImages = gig
        ? [
              { id: 1, main: gig.cover, thumbnail: gig.cover, alt: gig.title },
              gig.firstThumbnail && { id: 2, main: gig.firstThumbnail, thumbnail: gig.firstThumbnail, alt: `${gig.title} 2` },
              gig.secondThumbnail && { id: 3, main: gig.secondThumbnail, thumbnail: gig.secondThumbnail, alt: `${gig.title} 3` },
              gig.thirdThumbnail && { id: 4, main: gig.thirdThumbnail, thumbnail: gig.thirdThumbnail, alt: `${gig.title} 4` },
          ].filter(Boolean)
        : [];

    // Build pricing plans from gig.pricing
    const pricingPlans = gig?.pricing
        ? Object.values(gig.pricing).map((plan) => ({
              title: plan.title,
              name: plan.name,
              price: `$${plan.price}`,
              description: plan.description,
              deliveryTimeframe: plan.deliveryTimeframe,
              features: [plan.description, `Delivery: ${plan.deliveryTimeframe}`],
              benefits: {
                  unlimitedRevisions: ['advanced', 'pro', 'premium'].includes(plan.name),
                  functionalWebsite: true,
                  paymentProcess: ['standard', 'advanced', 'pro', 'premium'].includes(plan.name),
                  legalForms: ['pro', 'premium'].includes(plan.name),
              },
          }))
        : [];

    const [isMobile, setIsMobile] = useState(false);
    const moreGigsRef = useRef(null);

    // Autoplay + resize
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        handleResize();
        window.addEventListener('resize', handleResize);

        if (autoplay && galleryImages.length > 0) {
            autoplayRef.current = setInterval(() => {
                setCurrentImage((prev) => (prev + 1) % galleryImages.length);
            }, 5000);
        }

        return () => {
            window.removeEventListener('resize', handleResize);
            if (autoplayRef.current) clearInterval(autoplayRef.current);
        };
    }, [autoplay, galleryImages.length]);

    const handleThumbnailClick = (index) => {
        setCurrentImage(index);
        if (autoplayRef.current) clearInterval(autoplayRef.current);
        if (autoplay) {
            autoplayRef.current = setInterval(() => {
                setCurrentImage((prev) => (prev + 1) % galleryImages.length);
            }, 5000);
        }
    };

    const scrollMoreGigs = (direction) => {
        const container = moreGigsRef.current;
        if (!container) return;
        const scrollAmount = direction === 'left' ? -container.offsetWidth : container.offsetWidth;
        container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="text-center">
                        <div className="w-10 h-10 border-4 border-[#174568] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !gig) {
        return (
            <>
                <Header />
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="text-center">
                        <p className="text-red-500 text-lg font-medium">{error || 'Gig not found'}</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <GD
                gig={gig}
                isMobile={isMobile}
                galleryImages={galleryImages}
                currentImage={currentImage}
                handleThumbnailClick={handleThumbnailClick}
                pricingPlans={pricingPlans}
                scrollMoreGigs={scrollMoreGigs}
                moreGigsRef={moreGigsRef}
                similarGigs={similarGigs}
                reviews={reviews}
                reviewStats={reviewStats}
            />
            <Footer />
        </>
    );
};

export default GigDetails;